import {useQuery} from '@tanstack/react-query';
import {Alert, Box, CircularProgress, Typography} from '@mui/material';
import NumericPair from '../../components/NumericPair';
import Section from '../../components/Section';
import {fetchZeroNeetSlugs} from '../utils/helpers';
import {buildDashboard3Slugs, DASHBOARD3_AGGREGATE_TOOLTIP, DASHBOARD3_NATURE_TOOLTIP, FINALITA} from '../utils/slugs';
import {ZeroNeetDashboardData} from '../utils/types';

interface Props {
    container: string;
    uoaId: string;
    survey504Id: string;
}

const Dashboard3 = ({container, uoaId, survey504Id}: Props) => {
    const slugs = buildDashboard3Slugs(Number(survey504Id));
    const aggregateSlug = slugs.find((slug) => slug.key === 'ind_3_1')!;
    const natureSlugs = slugs.filter((slug) => slug.key !== 'ind_3_1');

    const {data, isLoading, isError} = useQuery<ZeroNeetDashboardData>({
        queryKey: ['zero-neet-dashboard3', container, uoaId, survey504Id],
        queryFn: () => fetchZeroNeetSlugs(container, uoaId, slugs),
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    if (isError || !data) {
        return <Alert severity='error'>Errore nel caricamento dei dati.</Alert>;
    }

    return (
        <Box>
            <Section title='DIMENSIONE RETE DI COLLABORAZIONE' tooltip={DASHBOARD3_AGGREGATE_TOOLTIP}>
                <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2, maxWidth: 360}}>
                    <NumericPair
                        title={aggregateSlug.title}
                        uoaValue={data[aggregateSlug.key].uoa}
                        averageValue={data[aggregateSlug.key].average}
                        isCount
                    />
                </Box>
            </Section>

            <Section
                title='COMPOSIZIONE PREVISTA DELLA RETE DI COLLABORAZIONE PER NATURA GIURIDICA DEGLI ENTI RISPETTO ALLA FINALITÀ'
                tooltip={DASHBOARD3_NATURE_TOOLTIP}
            >
                {(Object.keys(FINALITA) as (keyof typeof FINALITA)[]).map((finKey) => (
                    <Box key={finKey} sx={{mb: 2.5}}>
                        <Typography variant='subtitle2' color='text.secondary' sx={{mb: 1}}>
                            {FINALITA[finKey]}
                        </Typography>
                        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2}}>
                            {natureSlugs
                                .filter((slug) => slug.key.endsWith(`_${finKey}`))
                                .map((slug) => (
                                    <NumericPair key={slug.key} title={slug.title} uoaValue={data[slug.key].uoa} averageValue={data[slug.key].average} isCount={false} />
                                ))}
                        </Box>
                    </Box>
                ))}
            </Section>
        </Box>
    );
};

export default Dashboard3;
